import React, { useState, useContext, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Check, ChevronRight, User, Briefcase, MapPin, CreditCard, AlertCircle, ArrowLeft } from 'lucide-react';
import LocationMapPicker from '../components/LocationMapPicker';
import FileUploadPlaceholder from '../components/FileUploadPlaceholder';
import { SERVICE_CATEGORIES, SWISS_CANTONS } from '../constants/categories';


const errorMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((item) => item.msg).join(' · ');
  if (detail && typeof detail === 'object') return detail.message || fallback;
  return detail || fallback;
};

const AddRolePage = () => {
  const { targetRole } = useParams();
  const { user, API, language, refreshUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [verification, setVerification] = useState(null);
  const copy = {
    en: { pending: 'Pending manual review', pendingBody: 'Your application is locked while an administrator reviews it.', corrections: 'Corrections required', declined: 'Application declined', reviewer: 'Reviewer feedback', back: 'Back to settings', submitFailed: 'Setup could not be submitted. Review the required fields.', missingAreas: 'Select at least one service canton.', review: 'Manual review summary', ready: 'Ready to submit', paymentSeparate: 'Payment is separate and becomes available only after admin approval.', pendingAfter: 'Pending review appears only after this submission succeeds.', submit: 'Submit for manual review', submitting: 'Submitting…', approved: 'Manual verification approved', approvedBody: 'Activate the CHF 99 yearly professional subscription to add the role and begin bidding.', activate: 'Activate professional subscription — CHF 99/year', opening: 'Opening secure checkout…', sandbox: 'Preview checkout is waiting for the Saferpay test environment. No payment was started.' },
    de: { pending: 'Manuelle Prüfung ausstehend', pendingBody: 'Ihr Antrag ist während der Prüfung durch die Administration gesperrt.', corrections: 'Korrekturen erforderlich', declined: 'Antrag abgelehnt', reviewer: 'Rückmeldung der Prüfung', back: 'Zurück zu Einstellungen', submitFailed: 'Der Antrag konnte nicht eingereicht werden. Prüfen Sie die Pflichtangaben.', missingAreas: 'Wählen Sie mindestens einen Einsatzkanton.', review: 'Zusammenfassung für die manuelle Prüfung', ready: 'Bereit zum Einreichen', paymentSeparate: 'Die Zahlung erfolgt getrennt und ist erst nach der Genehmigung verfügbar.', pendingAfter: 'Der Status „Prüfung ausstehend“ erscheint erst nach erfolgreicher Einreichung.', submit: 'Zur manuellen Prüfung einreichen', submitting: 'Wird eingereicht…', approved: 'Manuelle Verifizierung genehmigt', approvedBody: 'Aktivieren Sie das Profi-Abonnement für CHF 99/Jahr, um die Rolle hinzuzufügen und Angebote abzugeben.', activate: 'Profi-Abonnement aktivieren — CHF 99/Jahr', opening: 'Sicherer Checkout wird geöffnet…', sandbox: 'Der Vorschau-Checkout wartet auf die Saferpay-Testumgebung. Es wurde keine Zahlung gestartet.' },
    it: { pending: 'Revisione manuale in attesa', pendingBody: 'La domanda è bloccata durante la revisione amministrativa.', corrections: 'Correzioni richieste', declined: 'Domanda rifiutata', reviewer: 'Feedback del revisore', back: 'Torna alle impostazioni', submitFailed: 'Impossibile inviare la domanda. Controlla i campi obbligatori.', missingAreas: 'Seleziona almeno un cantone di servizio.', review: 'Riepilogo per la revisione manuale', ready: 'Pronto per l’invio', paymentSeparate: 'Il pagamento è separato e diventa disponibile dopo l’approvazione.', pendingAfter: 'Lo stato in revisione appare solo dopo un invio riuscito.', submit: 'Invia per la revisione manuale', submitting: 'Invio…', approved: 'Verifica manuale approvata', approvedBody: 'Attiva l’abbonamento professionale da CHF 99/anno per aggiungere il ruolo e iniziare a fare offerte.', activate: 'Attiva abbonamento professionale — CHF 99/anno', opening: 'Apertura del checkout sicuro…', sandbox: 'Il checkout di anteprima attende l’ambiente di test Saferpay. Nessun pagamento è stato avviato.' },
    fr: { pending: 'Examen manuel en attente', pendingBody: 'Votre dossier est verrouillé pendant son examen par un administrateur.', corrections: 'Corrections requises', declined: 'Dossier refusé', reviewer: 'Retour de l’examinateur', back: 'Retour aux paramètres', submitFailed: 'Le dossier n’a pas pu être soumis. Vérifiez les champs requis.', missingAreas: 'Choisissez au moins un canton de service.', review: 'Résumé pour la vérification manuelle', ready: 'Prêt à soumettre', paymentSeparate: 'Le paiement est séparé et devient disponible après approbation.', pendingAfter: 'Le statut en attente apparaît uniquement après un envoi réussi.', submit: 'Soumettre à la vérification manuelle', submitting: 'Envoi…', approved: 'Vérification manuelle approuvée', approvedBody: 'Activez l’abonnement professionnel à CHF 99/an pour ajouter le rôle et commencer à faire des offres.', activate: 'Activer l’abonnement professionnel — CHF 99/an', opening: 'Ouverture du paiement sécurisé…', sandbox: 'Le paiement de prévisualisation attend l’environnement de test Saferpay. Aucun paiement n’a été lancé.' },
  }[language] || { pending: 'Pending manual review', pendingBody: 'Your application is locked while an administrator reviews it.', corrections: 'Corrections required', declined: 'Application declined', reviewer: 'Reviewer feedback', back: 'Back to settings', submitFailed: 'Setup could not be submitted. Review the required fields.', missingAreas: 'Select at least one service canton.', review: 'Manual review summary', ready: 'Ready to submit', paymentSeparate: 'Payment is separate and becomes available only after admin approval.', pendingAfter: 'Pending review appears only after this submission succeeds.', submit: 'Submit for manual review', submitting: 'Submitting…', approved: 'Manual verification approved', approvedBody: 'Activate the CHF 99 yearly professional subscription to add the role and begin bidding.', activate: 'Activate professional subscription — CHF 99/year', opening: 'Opening secure checkout…', sandbox: 'Preview checkout is waiting for the Saferpay test environment. No payment was started.' };
  const labels = {
    en: { services: 'Services', location: 'Location', documents: 'Documents', review: 'Review', payment: 'Payment', titlePro: 'Add Professional Profile', titleClient: 'Add Client Profile', subtitle: 'Complete setup to activate this account', categories: 'Service Categories *', categoryHelp: 'Select all services you provide', categoryMissing: 'Select at least one category', experience: 'Years of Experience', areaHelp: 'Select cantons where you provide services', workLocation: 'Work Location', verificationDocs: 'Verification Documents', back: 'Back', continue: 'Continue', profileDocs: 'Profile and required private documents' },
    de: { services: 'Dienste', location: 'Standort', documents: 'Dokumente', review: 'Prüfung', payment: 'Zahlung', titlePro: 'Profi-Profil hinzufügen', titleClient: 'Kundenprofil hinzufügen', subtitle: 'Schliessen Sie die Einrichtung ab, um dieses Konto zu aktivieren', categories: 'Dienstleistungskategorien *', categoryHelp: 'Wählen Sie alle angebotenen Dienste', categoryMissing: 'Wählen Sie mindestens eine Kategorie', experience: 'Jahre Berufserfahrung', areaHelp: 'Wählen Sie Ihre Einsatzkantone', workLocation: 'Arbeitsort', verificationDocs: 'Verifizierungsdokumente', back: 'Zurück', continue: 'Weiter', profileDocs: 'Profil und erforderliche private Dokumente' },
    it: { services: 'Servizi', location: 'Posizione', documents: 'Documenti', review: 'Revisione', payment: 'Pagamento', titlePro: 'Aggiungi profilo professionale', titleClient: 'Aggiungi profilo cliente', subtitle: 'Completa la configurazione per attivare questo account', categories: 'Categorie di servizio *', categoryHelp: 'Seleziona tutti i servizi offerti', categoryMissing: 'Seleziona almeno una categoria', experience: 'Anni di esperienza', areaHelp: 'Seleziona i cantoni di servizio', workLocation: 'Luogo di lavoro', verificationDocs: 'Documenti di verifica', back: 'Indietro', continue: 'Continua', profileDocs: 'Profilo e documenti privati richiesti' },
    fr: { services: 'Services', location: 'Localisation', documents: 'Documents', review: 'Vérification', payment: 'Paiement', titlePro: 'Ajouter un profil professionnel', titleClient: 'Ajouter un profil client', subtitle: 'Terminez la configuration pour activer ce compte', categories: 'Catégories de services *', categoryHelp: 'Sélectionnez tous les services proposés', categoryMissing: 'Choisissez au moins une catégorie', experience: 'Années d’expérience', areaHelp: 'Choisissez les cantons desservis', workLocation: 'Lieu de travail', verificationDocs: 'Documents de vérification', back: 'Retour', continue: 'Continuer', profileDocs: 'Profil et documents privés requis' },
  }[language] || { services: 'Services', location: 'Location', documents: 'Documents', review: 'Review', payment: 'Payment', titlePro: 'Add Professional Profile', titleClient: 'Add Client Profile', subtitle: 'Complete setup to activate this account', categories: 'Service Categories *', categoryHelp: 'Select all services you provide', categoryMissing: 'Select at least one category', experience: 'Years of Experience', areaHelp: 'Select cantons where you provide services', workLocation: 'Work Location', verificationDocs: 'Verification Documents', back: 'Back', continue: 'Continue', profileDocs: 'Profile and required private documents' };
  const documentLabels = {
    en: { languages: 'Languages Spoken', areas: 'Service Areas (Cantons) *', uploadHelp: 'Upload the required private documents for manual review.', photo: 'Profile Photo', logo: 'Company Logo', license: 'Business License', id: 'ID Document', insurance: 'Insurance Document', optional: 'optional', country: 'Document country *', idType: 'ID document type *', choose: 'Choose…', identity: 'Identity card', passport: 'Passport', permit: 'Residence permit' },
    de: { languages: 'Gesprochene Sprachen', areas: 'Einsatzgebiete (Kantone) *', uploadHelp: 'Laden Sie die erforderlichen privaten Dokumente für die manuelle Prüfung hoch.', photo: 'Profilfoto', logo: 'Firmenlogo', license: 'Handelsregisterauszug oder Berufslizenz', id: 'Identitätsdokument', insurance: 'Versicherungsnachweis', optional: 'optional', country: 'Land des Dokuments *', idType: 'Art des Ausweises *', choose: 'Auswählen…', identity: 'Identitätskarte', passport: 'Reisepass', permit: 'Aufenthaltsbewilligung' },
    it: { languages: 'Lingue parlate', areas: 'Aree di servizio (Cantoni) *', uploadHelp: 'Carica i documenti privati richiesti per la revisione manuale.', photo: 'Foto profilo', logo: 'Logo aziendale', license: 'Registro commerciale o licenza professionale', id: 'Documento d’identità', insurance: 'Documento assicurativo', optional: 'opzionale', country: 'Paese del documento *', idType: 'Tipo di documento *', choose: 'Scegli…', identity: 'Carta d’identità', passport: 'Passaporto', permit: 'Permesso di soggiorno' },
    fr: { languages: 'Langues parlées', areas: 'Zones de service (Cantons) *', uploadHelp: 'Importez les documents privés requis pour la vérification manuelle.', photo: 'Photo de profil', logo: 'Logo de l’entreprise', license: 'Registre du commerce ou licence professionnelle', id: 'Pièce d’identité', insurance: 'Attestation d’assurance', optional: 'facultatif', country: 'Pays du document *', idType: 'Type de pièce d’identité *', choose: 'Choisir…', identity: 'Carte d’identité', passport: 'Passeport', permit: 'Permis de séjour' },
  }[language] || { languages: 'Languages Spoken', areas: 'Service Areas (Cantons) *', uploadHelp: 'Upload the required private documents for manual review.', photo: 'Profile Photo', logo: 'Company Logo', license: 'Business License', id: 'ID Document', insurance: 'Insurance Document', optional: 'optional', country: 'Document country *', idType: 'ID document type *', choose: 'Choose…', identity: 'Identity card', passport: 'Passport', permit: 'Residence permit' };

  const [formData, setFormData] = useState({
    categories: [],
    service_areas: { type: 'cantons', values: [] },
    work_location: { city: '', canton: '', country: 'Switzerland' },
    languages: ['en'],
    years_experience: '',
    map_location: { lat: 46.8182, lng: 8.2275 },
    service_radius: 30,
    profile_photo_url: '',
    company_logo_url: '',
    business_license_url: '',
    id_document_url: '',
    insurance_url: '',
    references_urls: [],
    id_verification: { country: '', documentType: '', documentUrl: '' },
  });

  useEffect(() => {
    if (!user) { navigate('/auth'); return; }
    const currentRoles = user.roles || [user.role];
    if (currentRoles.includes(targetRole)) {
      toast.info(`You already have the ${targetRole} role`);
      navigate(targetRole === 'client' ? '/dashboard/client' : '/dashboard/professional');
    }
  }, [user, targetRole, navigate]);

  useEffect(() => {
    if (targetRole !== 'professional' || user?.pending_role_setup !== 'professional') return;
    Promise.all([
      axios.get(`${API}/professional-verification/me`),
      axios.get(`${API}/professionals/me`),
    ]).then(([verificationResponse, profileResponse]) => {
      const record = verificationResponse.data;
      const profile = profileResponse.data;
      setVerification(record);
      if (['changes_requested', 'rejected'].includes(record.status)) setStep(3);
      if (record.status === 'submitted') setStep(4);
      setFormData((previous) => ({
        ...previous,
        categories: profile.categories || [], service_areas: profile.service_areas || previous.service_areas,
        work_location: profile.work_location || previous.work_location, languages: profile.languages || previous.languages,
        years_experience: profile.years_experience || '', profile_photo_url: profile.profile_photo_url || '', company_logo_url: profile.company_logo_url || '',
        business_license_url: record.documents?.business_license_url || '', id_document_url: record.documents?.id_document_url || '', insurance_url: record.documents?.insurance_url || '',
        id_verification: { country: record.identity?.country || '', documentType: record.identity?.document_type || '', documentUrl: record.documents?.id_document_url || '' },
      }));
    }).catch(() => {});
  }, [API, targetRole, user?.pending_role_setup]);

  const totalSteps = targetRole === 'professional' ? 4 : 3;

  const handleSetupRole = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/users/setup-role`, { role: targetRole, ...formData });
      if (targetRole === 'professional') {
        const response = await axios.post(`${API}/professional-verification/me/submit`);
        setVerification(response.data);
        await refreshUser?.();
        toast.success(language === 'de' ? 'Antrag zur manuellen Prüfung eingereicht.' : language === 'it' ? 'Domanda inviata per la revisione manuale.' : language === 'fr' ? 'Dossier soumis à la vérification manuelle.' : 'Application submitted for manual review.');
        return;
      }
      const payRes = await axios.post(`${API}/saferpay/add-role-subscription?target_role=${targetRole}`);
      if (payRes.data.redirect_url) {
        localStorage.setItem('saferpay_token', payRes.data.token);
        localStorage.setItem('saferpay_return_page', `/add-role/${targetRole}`);
        window.location.href = payRes.data.redirect_url;
      } else {
        throw new Error('No redirect URL from payment');
      }
    } catch (err) {
      toast.error(errorMessage(err, copy.submitFailed));
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (val) => {
    const cats = formData.categories;
    setFormData({ ...formData, categories: cats.includes(val) ? cats.filter(c => c !== val) : [...cats, val] });
  };

  const toggleCanton = (val) => {
    const areas = formData.service_areas.values;
    setFormData({ ...formData, service_areas: { type: 'cantons', values: areas.includes(val) ? areas.filter(a => a !== val) : [...areas, val] } });
  };

  const toggleLanguage = (lang) => {
    const langs = formData.languages;
    if (langs.includes(lang) && langs.length === 1) return;
    setFormData({ ...formData, languages: langs.includes(lang) ? langs.filter(l => l !== lang) : [...langs, lang] });
  };

  const steps = [
    { label: labels.services, icon: Briefcase },
    { label: labels.location, icon: MapPin },
    ...(targetRole === 'professional' ? [{ label: labels.documents, icon: User }] : []),
    { label: targetRole === 'professional' ? labels.review : labels.payment, icon: targetRole === 'professional' ? Check : CreditCard },
  ];

  const isStep1Valid = targetRole !== 'professional' || formData.categories.length > 0;
  const isDocumentsValid = Boolean(formData.profile_photo_url && formData.business_license_url && formData.id_document_url && formData.id_verification.country && formData.id_verification.documentType);

  const startApprovedCheckout = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/saferpay/add-role-subscription?target_role=professional`);
      localStorage.setItem('saferpay_token', response.data.token);
      localStorage.setItem('saferpay_return_page', '/add-role/professional');
      window.location.href = response.data.redirect_url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail?.code === 'payment_sandbox_required' ? copy.sandbox : errorMessage(err, copy.submitFailed));
      setLoading(false);
    }
  };

  if (targetRole === 'professional' && verification?.status === 'approved') {
    return <main className="min-h-screen bg-gray-50 px-4 py-12" data-testid="add-role-approved-checkout"><div className="mx-auto max-w-xl border border-emerald-300 bg-white p-6 shadow-lg sm:p-8"><Check className="h-10 w-10 text-emerald-700" /><h1 className="mt-4 text-2xl font-bold text-gray-900">{copy.approved}</h1><p className="mt-2 text-gray-600">{copy.approvedBody}</p><Button onClick={startApprovedCheckout} disabled={loading} className="mt-6 h-auto min-h-11 w-full whitespace-normal bg-emerald-800 py-3 text-white hover:bg-emerald-900" data-testid="add-role-approved-payment-button"><CreditCard className="mr-2 h-4 w-4 shrink-0" />{loading ? copy.opening : copy.activate}</Button></div></main>;
  }

  if (targetRole === 'professional' && verification?.status === 'submitted') {
    return <main className="min-h-screen bg-gray-50 px-4 py-12" data-testid="add-role-submitted-page"><div className="mx-auto max-w-xl border border-blue-300 bg-white p-8 shadow-lg"><Check className="h-10 w-10 text-blue-700" /><h1 className="mt-4 text-2xl font-bold text-gray-900">{copy.pending}</h1><p className="mt-2 text-gray-600">{copy.pendingBody}</p><Button onClick={() => navigate('/settings')} variant="outline" className="mt-6" data-testid="add-role-submitted-settings-button">{copy.back}</Button></div></main>;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">

        {/* Back to Settings */}
        <div className="mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/settings')}
            className="text-gray-500 hover:text-gray-700 -ml-2"
            data-testid="add-role-back-to-settings-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            {language === 'de' ? 'Zurück zu Einstellungen' : language === 'it' ? 'Torna alle Impostazioni' : language === 'fr' ? 'Retour aux Paramètres' : 'Back to Settings'}
          </Button>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          {targetRole === 'professional' ? (
            <Briefcase className="w-10 h-10 text-red-600 mx-auto mb-3" />
          ) : (
            <User className="w-10 h-10 text-blue-600 mx-auto mb-3" />
          )}
          <h1 className="text-2xl font-bold text-gray-900">{targetRole === 'professional' ? labels.titlePro : labels.titleClient}</h1>
          <p className="text-gray-500 text-sm mt-1">{labels.subtitle}</p>
        </div>

        {['changes_requested', 'rejected'].includes(verification?.status) && <div className="mb-6 border border-amber-300 bg-amber-50 p-4 text-amber-950" role="alert" data-testid="add-role-correction-reason"><p className="font-semibold">{verification.status === 'rejected' ? copy.declined : copy.corrections}</p><p className="mt-1 text-sm"><span className="font-medium">{copy.reviewer}: </span>{verification.decision_reason}</p></div>}

        {/* Step indicators */}
        <div className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2 mb-8" data-testid="add-role-step-indicators">
          {steps.map((s, i) => {
            const StepIcon = s.icon;
            const stepNum = i + 1;
            const isDone = step > stepNum;
            const isActive = step === stepNum;
            return (
              <React.Fragment key={s.label}>
                <div className={`flex items-center gap-1 px-2 sm:gap-1.5 sm:px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  isDone ? 'bg-green-100 text-green-700' : isActive ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-400'
                }`}>
                  {isDone ? <Check className="w-3 h-3" /> : <StepIcon className="w-3 h-3" />}
                  {s.label}
                </div>
                {i < steps.length - 1 && <ChevronRight className="w-3 h-3 text-gray-300 flex-shrink-0" />}
              </React.Fragment>
            );
          })}
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 md:p-8">

          {/* STEP 1: Categories (professional) or simple setup (client) */}
          {step === 1 && (
            <div className="space-y-6">
              {targetRole === 'professional' ? (
                <>
                  <div>
                    <Label className="text-base font-semibold">{labels.categories}</Label>
                    <p className="text-xs text-gray-500 mb-3">{labels.categoryHelp}</p>
                    <div className="max-h-80 overflow-y-auto border border-gray-100 rounded-lg p-3">
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {SERVICE_CATEGORIES.map((cat) => {
                          const label = (language === 'de' ? cat.de : cat.en).split('(')[0].trim();
                          return (
                            <button
                              key={cat.value}
                              type="button"
                              data-testid={`add-role-cat-${cat.value}`}
                              onClick={() => toggleCategory(cat.value)}
                              className={`px-2 py-2 rounded-lg text-xs font-medium border-2 transition-all ${
                                formData.categories.includes(cat.value)
                                  ? 'bg-red-600 text-white border-red-600'
                                  : 'bg-white text-gray-700 border-gray-200 hover:border-red-300'
                              }`}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {formData.categories.length === 0 && (
                      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> {labels.categoryMissing}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label className="text-base font-semibold">{labels.experience}</Label>
                    <Input
                      type="number" min="0" max="50"
                      value={formData.years_experience}
                      onChange={(e) => setFormData({ ...formData, years_experience: e.target.value })}
                      placeholder="e.g. 5"
                      className="mt-1 w-32"
                      data-testid="add-role-years-exp"
                    />
                  </div>

                  <div>
                    <Label className="text-base font-semibold">{documentLabels.languages}</Label>
                    <div className="flex gap-2 mt-2">
                      {['en', 'de', 'fr', 'it'].map(lang => (
                        <button key={lang} type="button" onClick={() => toggleLanguage(lang)}
                          className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                            formData.languages.includes(lang)
                              ? 'bg-red-600 text-white border-red-600'
                              : 'bg-white text-gray-600 border-gray-300 hover:border-red-300'
                          }`}
                        >
                          {lang.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <User className="w-16 h-16 text-blue-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Add Client Profile</h3>
                  <p className="text-gray-500 text-sm max-w-sm mx-auto">
                    With a Client profile you can post jobs, hire professionals, and manage projects — all in one account.
                  </p>
                  <div className="mt-6 bg-blue-50 rounded-xl p-4 text-left space-y-2">
                    {['Post unlimited jobs', 'Chat with verified professionals', 'Secure payment handling', 'Access verified providers'].map(f => (
                      <div key={f} className="flex items-center gap-2 text-sm text-blue-800">
                        <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        {f}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Button
                data-testid="add-role-step1-next"
                onClick={() => setStep(2)}
                disabled={!isStep1Valid}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
              >
                {labels.continue} <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}

          {/* STEP 2: Location & Service Areas */}
          {step === 2 && (
            <div className="space-y-6">
              {targetRole === 'professional' && (
                <>
                  <div>
                    <Label className="text-base font-semibold">{documentLabels.areas}</Label>
                    <p className="text-xs text-gray-500 mb-2">{labels.areaHelp}</p>
                    <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto border border-gray-100 rounded-lg p-2">
                      {SWISS_CANTONS.map((canton) => (
                        <button key={canton.value} type="button"
                          data-testid={`add-role-canton-${canton.value}`}
                          onClick={() => toggleCanton(canton.value)}
                          className={`px-2 py-1 rounded text-xs border transition-colors ${
                            formData.service_areas.values.includes(canton.value)
                              ? 'bg-red-600 text-white border-red-600'
                              : 'bg-white text-gray-600 border-gray-200 hover:border-red-300'
                          }`}
                        >
                          {canton.en}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-base font-semibold">{labels.workLocation}</Label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <Input
                        placeholder="City" value={formData.work_location.city}
                        onChange={(e) => setFormData({ ...formData, work_location: { ...formData.work_location, city: e.target.value } })}
                        data-testid="add-role-city"
                      />
                      <Input
                        placeholder="Canton" value={formData.work_location.canton}
                        onChange={(e) => setFormData({ ...formData, work_location: { ...formData.work_location, canton: e.target.value } })}
                        data-testid="add-role-canton"
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="border-t pt-4">
                <LocationMapPicker
                  location={formData.map_location}
                  radius={formData.service_radius}
                  onLocationChange={(loc) => setFormData({ ...formData, map_location: loc })}
                  onRadiusChange={(r) => setFormData({ ...formData, service_radius: r })}
                  language={language}
                />
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1" data-testid="add-role-step2-back">{labels.back}</Button>
                <Button
                  data-testid="add-role-step2-next"
                  onClick={() => targetRole === 'professional' ? setStep(3) : setStep(totalSteps)}
                  disabled={targetRole === 'professional' && formData.service_areas.values.length === 0}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {labels.continue} <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              {targetRole === 'professional' && formData.service_areas.values.length === 0 && <p className="text-sm text-red-600" data-testid="add-role-missing-service-areas">{copy.missingAreas}</p>}
            </div>
          )}

          {/* STEP 3: Documents (Professional only) */}
          {step === 3 && targetRole === 'professional' && (
            <div className="space-y-5">
              <div>
                <Label className="text-base font-semibold">{labels.verificationDocs}</Label>
                <p className="text-xs text-gray-500 mt-1 mb-4">
                  {documentLabels.uploadHelp}
                </p>
              </div>

              {[
                { key: 'profile_photo_url', label: documentLabels.photo, accept: 'image/*', folder: 'profiles', required: true },
                { key: 'company_logo_url', label: documentLabels.logo, accept: 'image/*', folder: 'profiles', required: false },
                { key: 'business_license_url', label: documentLabels.license, accept: 'image/*,.pdf', folder: 'verification_docs', required: true },
                { key: 'id_document_url', label: documentLabels.id, accept: 'image/*,.pdf', folder: 'verification_docs', required: true },
                { key: 'insurance_url', label: documentLabels.insurance, accept: 'image/*,.pdf', folder: 'verification_docs', required: false },
              ].map(field => (
                <FileUploadPlaceholder
                  key={field.key}
                  label={`${field.label}${field.required ? '' : ` (${documentLabels.optional})`}`}
                  accept={field.accept}
                  value={formData[field.key]}
                  onChange={(url) => setFormData({ ...formData, [field.key]: url })}
                  language={language}
                  userId={user?.id || 'temp'}
                  folder={field.folder}
                  required={field.required}
                  testId={`add-role-${field.key.replaceAll('_', '-')}`}
                />
              ))}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="text-sm font-medium text-gray-800">{documentLabels.country}<select value={formData.id_verification.country} onChange={(e) => setFormData({ ...formData, id_verification: { ...formData.id_verification, country: e.target.value } })} className="mt-1 h-11 w-full border border-gray-300 bg-white px-3" data-testid="add-role-document-country"><option value="">{documentLabels.choose}</option><option value="CH">Switzerland</option><option value="DE">Germany</option><option value="FR">France</option><option value="IT">Italy</option><option value="AT">Austria</option></select></label>
                <label className="text-sm font-medium text-gray-800">{documentLabels.idType}<select value={formData.id_verification.documentType} onChange={(e) => setFormData({ ...formData, id_verification: { ...formData.id_verification, documentType: e.target.value } })} className="mt-1 h-11 w-full border border-gray-300 bg-white px-3" data-testid="add-role-document-type"><option value="">{documentLabels.choose}</option><option value="identity_card">{documentLabels.identity}</option><option value="passport">{documentLabels.passport}</option><option value="residence_permit">{documentLabels.permit}</option></select></label>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(2)} className="flex-1">{labels.back}</Button>
                <Button
                  data-testid="add-role-step3-next"
                  onClick={() => setStep(4)}
                  disabled={!isDocumentsValid}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {labels.continue} <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* FINAL STEP: review submission for professionals, payment for clients */}
          {step === totalSteps && (
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-xl p-5 border border-gray-200" data-testid="add-role-final-summary">
                <h3 className="font-semibold text-gray-800 mb-4">{targetRole === 'professional' ? copy.review : 'Order Summary'}</h3>
                <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center mb-3">
                  <span className="text-gray-600">
                    {targetRole === 'professional' ? labels.profileDocs : 'Client Plan — Yearly'}
                  </span>
                  <span className="font-bold text-gray-900">
                    {targetRole === 'professional' ? copy.ready : 'CHF 12 / year'}
                  </span>
                </div>
                {targetRole !== 'professional' && <div className="border-t border-gray-200 pt-3 flex justify-between items-center">
                  <span className="font-semibold text-gray-800">Total today</span>
                  <span className="text-xl font-bold text-gray-900">CHF 12.00</span>
                </div>}
              </div>

              <div className="space-y-2">
                <p className="text-xs text-gray-500 text-center">
                  {targetRole === 'professional' ? copy.paymentSeparate : 'You will be redirected to Worldline / Saferpay for secure payment'}
                </p>
                <p className="text-xs text-gray-400 text-center">
                  {targetRole === 'professional' ? copy.pendingAfter : 'Your new role will be activated after confirmed payment'}
                </p>
              </div>

              {verification?.status === 'submitted' && <div className="border border-blue-300 bg-blue-50 p-4 text-blue-950" role="status" data-testid="add-role-submitted-status"><p className="font-semibold">Pending manual review</p><p className="mt-1 text-sm">We will notify you after an administrator reviews your documents.</p></div>}

              <div className="flex flex-col gap-3 sm:flex-row">
                <Button variant="outline" onClick={() => setStep(totalSteps - 1)} className="h-auto min-h-11 flex-1 whitespace-normal">{labels.back}</Button>
                <Button
                  data-testid={targetRole === 'professional' ? 'add-role-submit-review-button' : 'add-role-pay-btn'}
                  onClick={handleSetupRole}
                  disabled={loading || verification?.status === 'submitted'}
                  className="h-auto min-h-11 flex-1 whitespace-normal bg-red-600 py-3 hover:bg-red-700 text-white"
                >
                  {targetRole === 'professional' ? <Check className="w-4 h-4 mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
                  {loading ? copy.submitting : targetRole === 'professional' ? copy.submit : 'Pay CHF 12'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddRolePage;
